import type { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import type { AccessPermission, ListWithTotal, RangeRequestParams } from "@entrilia/shared/api/types.ts";
import type { AnyObject, EmptyObject, Maybe } from "@entrilia/shared/types.ts";
import type { z } from "@entrilia/shared/zod.ts";
import type {
  cashFlowSettingsSchema,
  financialStatementGeneralSettingsSchema,
  listGeneralSettingsSchema,
  pivotGeneralSettingsSchema,
  tabularGeneralSettingsSchema,
  tileSettingsSchema,
} from "../schemas/biClientSchema.ts";
import type { ClientInfo } from "./clientTypes.ts";
import type {
  AmountType,
  CalculateByField,
  CalculationType,
  ConditionConfiguration,
  ConditionDescriptor,
  ConditionEqualityType,
  ConditionFilterDescriptor,
  FieldConfiguration,
  FieldCustomizations,
  FormatType,
  Grouping,
  MeasureDateRange,
  NumberFormatterOptions,
} from "./fieldConfigurationTypes.ts";

export interface CancellationToken {
  cancel: () => void;
}

interface ReportValidationResultBase {
  hasRequiredDataset: boolean;
  requiredDataSetName: string;
  name?: NameValidationResult;
  group?: GroupValidationResult;
  conditions?: ConditionValidationResult[] | null;
}

export interface PivotReportValidationResponse extends ReportValidationResultBase {
  reportType: "Pivot";
  rows?: DimensionValidationResult[] | null;
  columns?: DimensionValidationResult[] | null;
  measures?: MeasuresValidationResult[] | null;
}

export interface TabularReportValidationResponse extends ReportValidationResultBase {
  reportType: "Tabular";
  fields?: TabularFieldValidationResult[] | null;
}

export interface ChartReportValidationResponse extends ReportValidationResultBase {
  reportType: "Chart";
  arguments?: DimensionValidationResult[] | null;
  measures?: MeasuresValidationResult[] | null;
}

export interface TileReportValidationResponse extends ReportValidationResultBase {
  reportType: "Tile";
  fields?: MeasuresValidationResult[] | null;
}

export interface ListReportValidationResponse extends ReportValidationResultBase {
  reportType: "List";
  fields?: MeasuresValidationResult[] | null;
}

interface TabularFieldValidationResult {
  guid: string;
  dimension?: DimensionValidationResult;
  measure?: MeasuresValidationResult;
}

interface GroupValidationResult {
  id?: string;
  name: string;
  exist: boolean;
}

interface NameValidationResult {
  exist: boolean;
}

export interface DimensionValidationResult extends FieldValidationResultBase {
  customConditions: ConditionValidationResult[];
}

export interface ConditionValidationResult extends FieldValidationResultBase {
  missedValues?: null[] | null;
}

export interface MeasuresValidationResult extends FieldValidationResultBase {
  measureUnits?: null[] | null;
  customConditions: ConditionValidationResult[];
  message?: string;
}

export interface FieldValidationResultBase {
  valid: boolean;
  message?: string;
  guid: string;
  caption?: string;
  name: string;
  isCustomField: boolean;
}

export interface ValidateReportRequest {
  reportId: string;
  fromCompanyCode: string;
  toCompanyCode: string;
  validateGroup: boolean;
}

export type ReportValidationResponse =
  | PivotReportValidationResponse
  | TabularReportValidationResponse
  | ChartReportValidationResponse
  | TileReportValidationResponse
  | ListReportValidationResponse;

export interface CopyReportRequest extends ValidateReportRequest {
  copyConditionValues: boolean;
  override: boolean;
  groupIdSelectedByUser?: string;
  companySharedAccess?: ReportAccessType;
  inheritFromParent?: boolean;
}

export interface CopyReportResponse {
  reportId?: string;
  hasRequiredDataset: boolean;
  requiredDataSetName: string;
}

export interface CreateReportGroup {
  name: string | undefined;
  toCompanyCode: string;
}

export type GenerateApiReportRequest = DuplicateReportRequest;
export type GenerateDashboardReportRequest = { reportId: string };
export type DuplicateReportRequest = Pick<Report, "clientCode" | "reportId" | "name" | "groupId">;
export type FormatterDescription = {
  title: string;
  subTitle: string | undefined;
  options: Partial<NumberFormatterOptions>;
};

export enum Ordering {
  None = "None",
  Ascending = "Ascending",
  Descending = "Descending",
}

export enum SortType {
  Dimension = "Dimension",
  Measure = "Measure",
  Custom = "Custom",
}

export enum MeasureUnitTable {
  NotSet = 0,
  Gl = 1 << 1, //2
  Memo = 1 << 2, //4
  ApAr = 1 << 3, //8
  Vat = 1 << 4, //16
}

export type ReportResponse = Report;
export type Report = {
  reportId: string;
  reportType: ReportType;
  clientCode: string;
  name: string;
  dataSetId: Maybe<string>;
  groupId?: string;
  createdAt: string;
  configuration: ReportConfiguration;
  authorization: ReportAuthorizationInfo;
  inheritFromParent?: boolean;
  reportUsage: ReportUsage;
  isDraft?: boolean;
};
export type ReportUsage = "Ui" | "Api" | "Dashboard" | "System";
export type ReportTemplate = Report & {
  fundTypes: FundType[];
  status: Status;
  dataSource: string;
  organization: string;
  description: string;
  isNew?: boolean;
};

export enum FundType {
  FundOfFunds = "FundOfFunds",
  PrivateEquity = "PrivateEquity",
  VentureCapital = "VentureCapital",
}

export enum Status {
  Live = "Live",
  Draft = "Draft",
}

export enum ReportType {
  Pivot = "Pivot",
  Tabular = "Tabular",
  CashFlow = "CashFlow",
  PieChart = "PieChart",
  BarChart = "BarChart",
  LineChart = "LineChart",
  AreaChart = "AreaChart",
  DoughnutChart = "DoughnutChart",
  TreeMapChart = "TreeMapChart",
  Tile = "Tile",
  List = "List",
  FinancialStatement = "FinancialStatement",
}

export const reportTypeToCaption = {
  [ReportType.Pivot]: "Pivot",
  [ReportType.Tabular]: "Tabular",
  [ReportType.CashFlow]: "IRR Details",
  [ReportType.PieChart]: "Pie Chart",
  [ReportType.BarChart]: "Bar Chart",
  [ReportType.LineChart]: "Line Chart",
  [ReportType.AreaChart]: "Area Chart",
  [ReportType.DoughnutChart]: "Doughnut Chart",
  [ReportType.TreeMapChart]: "Tree Map Chart",
  [ReportType.Tile]: "Tile",
  [ReportType.List]: "List",
  [ReportType.FinancialStatement]: "Financial Statement",
};

export enum CellPropertyEnum {
  None = "None",
  StartIcon = "StartIcon",
  Link = "Link",
  FileNames = "FileNames",
}

export type CellProperties = {
  [key in keyof typeof CellPropertyEnum]: string;
};
export type ReportGroupsResponse = Array<ReportGroup>;
export type ReportGroup = {
  id: string;
  caption: string;
  order: number;
  // Display projection of the group's sharing (resolved names), for rendering sharing icons on the folder row.
  sharedWith?: SharedWithEntity[];
};

export type PivotGeneralSettings = z.infer<typeof pivotGeneralSettingsSchema>;
export type BaseConfiguration = {
  conditions: ConditionConfiguration[];
};
export type ReportConfiguration =
  | PivotConfiguration
  | TabularConfiguration
  | ChartConfiguration
  | TileConfiguration
  | ListConfiguration
  | FinancialStatementConfiguration;

export interface ReportHistoryItem {
  id: string;
  configuration: ReportConfiguration;
  createdBy?: string;
  modifiedBy?: string;
  updateAt: string;
  createdAt: string;
  dataSetId: string;
  reportType: ReportType;
  isDeleted: boolean;
  versionNumber: number;
}

export type TemplateFieldsItem = {
  name: string;
  icon: MetaIcons;
  subItems: TemplateFieldsItem[];
};

export enum MetaIcons {
  Hash = "Hash",
  Calendar = "Calendar",
  Sigma = "Sigma",
  Function = "Function",
  CustomCalculation = "CustomCalculation",
}

export type TemplateInfoFieldsResponse = {
  fields: { [key: string]: TemplateFieldsItem[] };
};
export type PivotConfiguration = BaseConfiguration & {
  rows: FieldConfiguration[];
  columns: FieldConfiguration[];
  values: FieldConfiguration[];
  sort: SortConfiguration[];
  settings: PivotGeneralSettings;
};
export type SortConfiguration = {
  //This name is used to get the field from metadata.
  name: string;
  ordering: Ordering;
  caption: string | undefined | null;
  isGroupField?: boolean | undefined | null;
  id: string;
  type: SortType;
  pivotColumnId?: string;
};
export type ReportTemplateDto = {
  groupName: string;
  groupOrder: number;
} & ReportTemplate;
export type SummaryPlace = "Footer" | "Inline";
export type TabularSubtotalLabelPosition = "None" | "Group" | "Leading" | "Trailing";
export type PeriodType = "LastMonth" | "LastQuarter" | "LastYear";
export type TabularGeneralSettings = z.infer<typeof tabularGeneralSettingsSchema>;
export type CashFlowSettingsConfiguration = z.infer<typeof cashFlowSettingsSchema>;
export type TileSettings = z.infer<typeof tileSettingsSchema>;
export type ListGeneralSettings = z.infer<typeof listGeneralSettingsSchema>;
export type ListDataSettings = Pick<ListGeneralSettings, "showGrandTotal" | "hideBlankRows" | "hideBlankRowsColumnIds">;
export type FinancialStatementGeneralSettings = z.infer<typeof financialStatementGeneralSettingsSchema>;
export interface GroupingField {
  id: string;
  hideSummaries?: boolean;
  customizations?: FieldCustomizations | null;
}
export type RunTabularReportRequest = Pick<TabularConfiguration, "conditions" | "sort"> & {
  settings?: Partial<TabularGeneralSettings>;
  reportId: string;
  sessionId: string;
  withDrilldown: boolean;
  useQueryLogging?: boolean;
};
export type RunTileReportRequest = Pick<TileConfiguration, "conditions"> & {
  reportId: string;
  sessionId: string;
  useQueryLogging?: boolean;
};
export type TabularConfiguration = BaseConfiguration & {
  conditions: ConditionConfiguration[];
  fields: FieldConfiguration[];
  sort: SortConfiguration[];
  grouping: GroupingField[];
  settings: TabularGeneralSettings;
};
export type TileConfiguration = BaseConfiguration & {
  conditions: ConditionConfiguration[];
  fields: FieldConfiguration[];
  settings: TileSettings;
};
export type ListConfiguration = BaseConfiguration & {
  ledger?: string;
  conditions: ConditionConfiguration[];
  fields: FieldConfiguration[];
  sort: SortConfiguration[];
  settings: ListGeneralSettings;
};
export type RunListReportRequest = Pick<ListConfiguration, "conditions" | "sort"> & {
  reportId: string;
  sessionId: string;
  range: RangeRequestParams;
  useQueryLogging?: boolean;
};
export type BuildListReportRequest = Omit<ListConfiguration, "settings"> & {
  settings: ListDataSettings;
  reportId: string;
  sessionId: string;
  range: RangeRequestParams;
  useQueryLogging?: boolean;
};
export type ListDataCell = DrillDownCell & {
  fieldId: string;
};
export type ListDataResponse = {
  etag: string;
  failedMeasures: FailedMeasure[];
  rows: ListDataCell[][];
  grandTotalItem: ListDataCell[];
  total?: number;
  loggingItems?: QueryLoggingItem[];
};
export type StatementLineType = "Group" | "Amount" | "Dimension" | "Subtotal" | "Total" | "Formula" | "Spacer";
export type StatementDataColumnType = "Data" | "Formula";
export type StatementPeriodScope = "All" | "Selected";
export type StatementRowKind = "Line" | "Member";
export type StatementLineConfiguration = {
  guid: string;
  lineType: StatementLineType;
  level: number;
  customLabel?: string;
  format?: NumberFormatterOptions;
  grandTotalStyle: boolean;
  dateRange?: MeasureDateRange;
  showAsGroup: boolean;
  field?: FieldConfiguration;
  dimension?: FieldConfiguration;
  showSubtotal: boolean;
  lineIds: string[];
  formula?: string;
  formulaParameters: string[];
  blankIfMissingValue: boolean;
};
export type StatementAxisConfiguration = {
  field: FieldConfiguration;
  showTotal: boolean;
};
export type StatementDataColumnConfiguration = {
  guid: string;
  columnType: StatementDataColumnType;
  customLabel?: string;
  format?: NumberFormatterOptions;
  dateRange?: MeasureDateRange;
  periodScope?: StatementPeriodScope;
  periodLineGuids?: string[];
  customConditions: ConditionConfiguration[];
  linkedConditions: string[];
  dateOffsetMonths?: number;
  formula?: string;
  formulaParameters: string[];
  blankIfMissingValue: boolean;
};
export type StatementColumnsConfiguration = {
  axes: StatementAxisConfiguration[];
  dataColumns: StatementDataColumnConfiguration[];
};
export type FinancialStatementConfiguration = BaseConfiguration & {
  conditions: ConditionConfiguration[];
  lines: StatementLineConfiguration[];
  columns: StatementColumnsConfiguration;
  settings: FinancialStatementGeneralSettings;
};
export type BuildFinancialStatementReportRequest = FinancialStatementConfiguration & {
  reportId: string;
  sessionId: string;
  cacheSessionId: string;
  withDrillDown: boolean;
  useQueryLogging?: boolean;
};
export type RunFinancialStatementReportRequest = Pick<FinancialStatementConfiguration, "conditions"> & {
  reportId: string;
  sessionId: string;
  withDrillDown: boolean;
  useQueryLogging?: boolean;
};
export type StatementHeaderCell = {
  memberKey?: string;
  caption?: string;
  isTotal: boolean;
  span: number;
};
export type StatementHeaderRow = {
  axisName: string;
  cells: StatementHeaderCell[];
};
export type StatementColumn = {
  key: string;
  dataColumnId: string;
  axisMemberKeys: string[];
  dateValues: (string | null)[];
  monthOffset: number;
  periodCaption?: string;
  dateRange?: MeasureDateRange;
  isFormula: boolean;
  isTotal: boolean;
};
export type StatementValueCell = ValueCell & {
  value: number | null;
  dateRange?: MeasureDateRange;
};
export type StatementRow = {
  lineId: string;
  kind: StatementRowKind;
  memberKey?: string;
  memberCaption?: string;
  cells: Record<string, StatementValueCell>;
};
export type FinancialStatementResponse = {
  etag: string;
  errorCode?: string;
  headerRows: StatementHeaderRow[];
  columns: StatementColumn[];
  rows: StatementRow[];
  failedMeasures: FailedMeasure[];
  loggingItems?: QueryLoggingItem[];
};
export type LegendSettings = {
  visible?: boolean;
  horizontalAlignment?: "left" | "center" | "right";
  verticalAlignment?: "top" | "bottom";
  title?: string;
};
export type AxisSettings = {
  showLabel?: boolean;
  title?: string;
};
export type BasicSeriesSettings = {
  name: string;
  showLabel?: boolean;
  showConnector?: boolean;
};
export type SerieType = "Bar" | "Line" | "Area";
export type BarWidth = "Thin" | "Small" | "Medium" | "Large";
export type LineType = "Line" | "Spline" | "Stepline";
export type ValueAxisType = "Main" | "Secondary";
export type LinearSeriesSettings = BasicSeriesSettings & {
  serieType: SerieType;
  valueAxis?: ValueAxisType;
};
export type BarSeriesSettings = LinearSeriesSettings & {
  barWidth?: BarWidth;
};
export type BasicLineSeriesSettings = LinearSeriesSettings & {
  lineType?: LineType;
  showPoints?: boolean;
};
export type SeriesSettings = BarSeriesSettings | LinearSeriesSettings | BasicLineSeriesSettings | BasicSeriesSettings;
export type BasicSettings = {
  type: ReportType;
  detailedView?: boolean;
  showTooltip?: boolean;
  legend?: LegendSettings;
  series?: SeriesSettings[];
};
export type ValueAxis = AxisSettings & {
  name: ValueAxisType;
};
export type BasicLinearSettings = BasicSettings & {
  gridMode?: "none" | "horizontal" | "all";
  rotated?: boolean;
  argumentAxis?: AxisSettings;
  valueAxis?: ValueAxis[];
};
export type BarSettings = BasicLinearSettings;
export type LineSettings = BasicLinearSettings;
export type AreaSettings = BasicLinearSettings;
export type PieSettings = BasicSettings;
export type DoughnutSettings = BasicSettings & {
  thickness?: number;
};

export type ChartStyleSettings = BarSettings | LineSettings | AreaSettings | PieSettings | DoughnutSettings;
export type ChartConfiguration = BaseConfiguration & {
  arguments: FieldConfiguration[];
  values: FieldConfiguration[];
  sort: SortConfiguration[];
  settings: ChartStyleSettings;
};
export type CellDrillDownRef = {
  drillDownId: string;
  fieldId: string;
  fieldCaption: string;
  /** Set when this ref comes from a function call (e.g. "GrandTotal"). Undefined for plain field references. */
  functionName?: string;
  value?: number;
};
export type ValueCell = {
  value: unknown;
  formattedValue: string;
  drillDownId?: string;
};
export type TabularDataCell = ValueCell & {
  cashFlows?: CashFlowValue[];
  positiveCashFlowTotalFormatted?: string;
  negativeCashFlowTotalFormatted?: string;
  toolTip?: string;
  properties?: CellProperties;
};
export type ClientInfoExtended = ClientInfo & {
  organization: string;
};

export interface ClientsResponse {
  clients: ClientInfo[];
}

export type DrillDownColumn = { dimensionName: string };
export type NameValidationResponse = { nameExists?: boolean };
export type DeleteReportItem = Pick<Report, "clientCode" | "reportId">;
export type DeleteReportsRequest = DeleteReportItem[];
export type ValidateReportNameRequest = Pick<Report, "clientCode" | "name"> & { reportId?: string };
export type RenameReportRequest = Pick<Report, "clientCode" | "name" | "reportId">;
export type ChangeReportGroupRequest = Pick<Report, "clientCode" | "groupId" | "reportId">;
export type ChangeReportsGroupRequest = { clientCode: string; groupId: string; reportIds: string[] };
export type AddReportGroupRequest = { clientCode: string; group: ReportGroup };
export type UpdateReportGroupRequest = { clientCode: string; group: ReportGroup };
export type ReplaceReportGroupRequest = { clientCode: string; fromGroup: string; toGroup: string };
export type ReorderReportGroupsRequest = { clientCode: string; groups: ReportGroup[] };
export type DeleteReportGroupStatus = "Deleted" | "ReportsAttached";
export type DeleteReportGroupResult = { status: DeleteReportGroupStatus; reportCount: number };
export type AddReportTemplateRequest = { template: ReportTemplate };
export type AddReportTemplateResponse = ReportTemplate;
export type GetReportTemplatesResponse = Array<ReportTemplateDto>;
export type UpdateReportTemplateStatusRequest = { templateId: string; status: Status };
export type UpdateReportTemplateStatusResponse = ReportTemplate;
export type DeleteReportTemplatesRequest = { templates: { templateId: string; organization: string }[] };
export type ValidateTemplateNameRequest = Pick<ReportTemplate, "organization" | "name"> & { templateId?: string };
export type TemplateNameValidationResponse = { nameExists?: boolean };
export type GetReportTemplateResponse = ReportTemplate;
export type UpdateReportTemplateRequest = { template: ReportTemplate };
export type UpdateReportTemplateResponse = ReportTemplate;
export type UpdateReportTemplateDataSourceRequest = { dataSource: string; templateId: string };
export type UpdateReportTemplateDataSourceResponse = { dataSource: string };
export type GetReportTemplateForOrganizationRequest = { templateId: string; organization: string };
export type GetReportTemplateForCompanyRequest = { templateId: string };
export type GetTemplateFieldsDataRequest = { templateId: string };
export type DuplicateTemplateRequest = { organization: string; templateId: string; name: string; groupId: string };

export interface ReportInfo {
  clientCode: string;
  reportId: string;
  name: string;
  reportType: ReportType;
  groupId: string;
  updateAt: string;
  createdBy: string;
  modifiedBy: string;
  authorization: ReportAuthorizationInfo;
  inheritFromParent: boolean;
  reportUsage: ReportUsage;
}

export type SharedWithEntityType = "User" | "Group" | "Company";

export interface SharedWithEntity {
  name: string;
  type: SharedWithEntityType;
}

export interface ReportAuthorizationInfo {
  private: boolean;
  own: boolean;
  access: ReportAccessType;
  sharedWith: SharedWithEntity[];
}

export interface UserForSharing {
  userId: string;
  name: string;
  email: string;
  permissions: AccessPermission[];
}

export interface ReportAuthorization {
  users: UserBasedAuthorization[];
  groups: GroupBasedAuthorization[];
  companyShared: CompanySharedAuthorization;
}

export interface UserBasedAuthorization {
  userId: string;
  access: ReportAccessType;
}

export interface GroupBasedAuthorization {
  groupId: string;
  access: ReportAccessType;
}

export interface CompanySharedAuthorization {
  access: ReportAccessType;
}

export interface GroupForSharing {
  groupId: string;
  name: string;
  memberCount: number;
}

export interface GetReportAuthorizationResponse {
  authorization: ReportAuthorization;
  inheritFromParent: boolean;
  users: UserForSharing[];
  groups: GroupForSharing[];
}

export interface UpdateReportAuthorizationRequest {
  clientCode: string;
  authorization: ReportAuthorization;
  inheritFromParent: boolean;
}

export interface UpdateReportsAuthorizationRequest {
  clientCode: string;
  reportIds: string[];
  authorization?: ReportAuthorization;
  inheritFromParent: boolean;
}

export interface UpdateReportGroupAuthorizationRequest {
  authorization: ReportAuthorization;
}

type MeasureCapabilities = {
  isConfigurable: boolean;
  customConditionsAllowed: boolean;
  dateRangeCustomization: boolean;
  changeAmountField: boolean;
  changeCalculatedBy: boolean;
  chartOfAccountsAllowed: boolean;
  transferredBalanceAllowed: boolean;
  isScalar: boolean;
};
export type MeasureUnit = {
  measureBasedUnit: boolean;
  measureName?: string;
  filters?: ConditionDescriptor[];
  table?: MeasureUnitTable;
  calculateByField?: CalculateByField;
  amountType?: AmountType;
};
export type MetaDescriptorBase = {
  name: string;
  caption: string;
  type: ItemDataType;
  itemType: MetaItemType;
  icon: MetaIcons;
};
export interface DimensionConditionOptions {
  allowedReportTypes: ReportType[];
}

export type DimensionOptions = {
  asDateReference?: Maybe<boolean>;
  supportAggregation?: Maybe<boolean>;
  conditions?: DimensionConditionOptions;
  supportedColumnGrouping?: (Grouping | null)[];
};
export type DimensionDescriptor = MetaDescriptorBase & {
  itemType: MetaItemType.DIMENSION;
  keyFieldName: string;
  isNew: boolean;
  availableFields: string[];
  displayValues: string[];
  dictionary: DimensionDictionary[];
  defaultFormat?: MetaFormat;
  glAllowed: boolean;
  options?: DimensionOptions;
  dataModelNames?: string[];
  supportedLedgerNames?: string[];
  custom?: boolean;
};
export type MeasureDescriptor = MetaDescriptorBase & {
  itemType: MetaItemType.MEASURE;
  measureId?: string;
  capabilities: MeasureCapabilities;
  usage: MeasureUsage;
  units: { [key: string]: MeasureUnit };
  formula?: string;
  custom: boolean;
  group: string;
  subgroupId?: string;
  isLocked?: boolean;
  modifiedBy?: string;
  customConditions?: ConditionDescriptor[];
  configuration?: FieldConfiguration;
};
export type MetricDescriptor = MetaDescriptorBase & {
  itemType: MetaItemType.METRIC;
  id?: string;
  usage: MeasureUsage;
  group: string;
  customConditions?: ConditionDescriptor[];
  configuration?: FieldConfiguration;
};
export type CustomColumnDescriptor = MetaDescriptorBase & {
  itemType: MetaItemType.CUSTOM;
  configuration?: FieldConfiguration;
  usage: MeasureUsage;
  group: string;
};
export type AggregationDescriptor = MetaDescriptorBase & {
  itemType: MetaItemType.AGGREGATION;
  customConditions?: ConditionDescriptor[];
  configuration?: FieldConfiguration;
};
export type AnyDescriptor =
  DimensionDescriptor | MeasureDescriptor | MetricDescriptor | CustomColumnDescriptor | AggregationDescriptor;
export enum PredefinedFormulaType {
  PercentageOfTotal = "PercentageOfTotal",
  CustomCalculation = "CustomCalculation",
}
export type MetaDescriptors =
  DimensionDescriptor | MeasureDescriptor | MetricDescriptor | CustomColumnDescriptor | AggregationDescriptor;
export type DimensionDictionary = Record<string, string | number | null>;
export type DictionarySearchRequest = {
  dimensionName: string;
  range: RangeRequestParams;
  searchTermUserInput?: string;
  sort?: { fieldId: string; ascending: boolean };
  conditions?: ConditionDescriptor[];
  dimensionNames?: string[];
  measureNames?: string[];
  selectedValuesUserInput?: (string | null)[];
  allRows?: boolean;
  useQueryLogging?: boolean;
};
export type DictionarySearchResponse = ListWithTotal<DimensionDictionary> & {
  keyFieldName: string;
  loggingItems?: QueryLoggingItem[];
};

export enum ItemDataType {
  General = "General",
  Date = "Date",
  Numeric = "Numeric",
}

export enum MetaItemType {
  DIMENSION = "DIMENSION",
  MEASURE = "MEASURE",
  METRIC = "METRIC",
  CUSTOM = "CUSTOM",
  AGGREGATION = "AGGREGATION",
}

export enum MeasureUsage {
  General = "General",
}

type NumericFormat = NumberFormatterOptions & {
  formatType: FormatType.Numeric;
};
type TextFormat = {
  formatType: FormatType.None;
};
type DateFormat = {
  formatType: FormatType.Date;
};
type DateTimeFormat = {
  formatType: FormatType.DateTime;
};
type BlankIfZeroFormat = {
  formatType: FormatType.BlankIfZero;
};
type BlankIfFalseFormat = {
  formatType: FormatType.BlankIfFalse;
};
export type MetaFormat =
  TextFormat | NumericFormat | DateFormat | DateTimeFormat | BlankIfZeroFormat | BlankIfFalseFormat;

export enum DimensionFieldType {
  DIMENSION = "DIMENSION",
  MEASURE = "MEASURE",
  METRIC = "METRIC",
  CUSTOM = "CUSTOM",
}

export type BaseField<T extends MetaDescriptorBase> = MetaField<T> & {
  type:
    DimensionFieldType.DIMENSION | DimensionFieldType.MEASURE | DimensionFieldType.METRIC | DimensionFieldType.CUSTOM;
  config: FieldConfiguration;
  invalid?: boolean;
};
export type DimensionField = BaseField<DimensionDescriptor> & {
  type: DimensionFieldType.DIMENSION;
};
export type MeasureField = BaseField<MeasureDescriptor> & {
  type: DimensionFieldType.MEASURE;
};
export type MetricField = BaseField<MetricDescriptor> & {
  type: DimensionFieldType.METRIC;
};
export type CustomColumnField = BaseField<CustomColumnDescriptor> & {
  type: DimensionFieldType.CUSTOM;
};
export interface ConditionValidation {
  invalid: boolean;
  message?: string;
}

export type ConditionField = MetaField<DimensionDescriptor> & {
  config: ConditionConfiguration;
  linked?: boolean;
  hasLinks?: boolean;
  validation?: ConditionValidation;
};
export type SortField = MetaField<AnyDescriptor> & {
  config: SortConfiguration;
};
export type ReportField = DimensionField | MeasureField | MetricField | CustomColumnField;
export type SortableField = ReportField;

export enum AreaItemType {
  CONDITIONS = "CONDITIONS",
  DIMENSION = "DIMENSION",
  MEASURE = "MEASURE",
  METRIC = "METRIC",
  ROWS = "ROWS",
  COLUMNS = "COLUMNS",
  VALUES = "VALUES",
  FIELDS = "FIELDS",
  SORTS = "SORTS",
  GROUPS = "GROUPS",
  ARGUMENTS = "ARGUMENTS",
  AGGREGATION = "AGGREGATION",
  ACCOUNT = "ACCOUNT",
}

export type MetaField<T extends MetaDescriptorBase> = {
  meta: T;
  areaItemType: AreaItemType;
};
export interface TabularSnapshot {
  resultId: string;
  dataSetId?: string;
  expiresAt: string;
  detailCount: number;
  rootCount: number;
  groupFields: string[];
}

export interface TabularSnapshotPageRequest {
  resultId: string;
  parentIndex?: number;
  skip: number;
  take: number;
}

export type TabularRow = {
  id: string;
  index?: number;
  groupField?: string;
  childrenCount?: number;
  totals?: Record<string, TabularDataCell>;
  data: Record<string, TabularDataCell>;
};
export type DimensionGroup = {
  caption: string;
  dimensions: string[];
  groups?: DimensionGroup[];
  // Report types this group is offered for; a missing value means the group is available for every report type.
  supportedReportTypes?: ReportType[] | null;
};
export type DimensionsStructure = {
  dimensions: string[];
  groups: DimensionGroup[];
};
export type AccountingBook = {
  code: string;
  name: string;
};
export type PivotColumnResponse = {
  id?: "lines" | (string & EmptyObject);
  dimensionName: string;
  formattedValues: string[];
  value: string;
  columns: PivotColumnResponse[];
  toolTip?: string;
  measureGuid?: string;
};
export type RunPivotReportRequest = Pick<PivotConfiguration, "conditions" | "sort"> & {
  reportId: string;
  sessionId: string;
  withDrilldown: boolean;
  useQueryLogging?: boolean;
};
export type PivotRowValueCell = ValueCell & {
  id: string;
  value: number | undefined;
  formattedValue: string;
  level: number;
  drillDownId?: string;
};
export type PivotRowDimensionCell = {
  dimensionName: string;
  formattedValues: string[];
  value: string;
  level: number;
  toolTip?: string;
  properties?: CellProperties;
  rowId: number;
};
export type PivotRowResponse = { [key: string]: PivotRowDimensionCell | PivotRowValueCell };
export type PivotDataGrandTotals = { [key: string]: PivotRowValueCell };
export type PivotDataResponse = {
  etag: string;
  errorCode?: string;
  columns: PivotColumnResponse[];
  rows: PivotRowResponse[];
  grandTotals: PivotDataGrandTotals;
  loggingItems?: QueryLoggingItem[];
  failedMeasures: FailedMeasure[];
};
export type ChartResponse = {
  etag: string;
  errorCode?: string;
  result: ChartRowResponse[];
  loggingItems?: QueryLoggingItem[];
  failedMeasures: FailedMeasure[];
};
export type ChartRowResponse = { ["rowId"]: string } & { [key: string]: PivotRowDimensionCell | PivotRowValueCell };
export type RunChartReportRequest = Pick<ChartConfiguration, "conditions"> & {
  reportId: string;
  sessionId: string;
  withDrilldown: boolean;
  useQueryLogging?: boolean;
};
export type TileDataResponse = {
  etag: string;
  errorCode?: string;
  loggingItems?: QueryLoggingItem[];
  failedMeasures: FailedMeasure[];
  value?: TabularDataCell;
  delta?: TabularDataCell;
  period: PeriodType;
};
export type RunReportConfigurationBase = {
  conditions: ConditionField[];
  reportType: ReportType;
  showParameters: boolean;
  fieldIdsWithCustomConditionParameters: string[];
  dataSetId: string;
  books: AccountingBook[];
  isMultiBook: boolean;
  customConditionsMeta: DimensionDescriptor[];
};

export interface RunReportConfiguration<
  T extends
    | TabularGeneralSettings
    | PivotGeneralSettings
    | ChartStyleSettings
    | TileSettings
    | ListGeneralSettings
    | FinancialStatementGeneralSettings,
> extends RunReportConfigurationBase {
  settings: T;
}

export interface TabularReportConfigurationForRunning extends RunReportConfiguration<TabularGeneralSettings> {
  fields: ReportField[];
  grouping: GroupingField[];
  sort: SortField[];
}

export interface TileReportConfigurationForRunning extends RunReportConfiguration<TileSettings> {
  fields: ReportField[];
}

export interface ListReportConfigurationForRunning extends RunReportConfiguration<ListGeneralSettings> {
  fields: ReportField[];
  sort: SortField[];
}

export interface FinancialStatementReportConfigurationForRunning extends RunReportConfiguration<FinancialStatementGeneralSettings> {
  lines: StatementLineConfiguration[];
  columns: StatementColumnsConfiguration;
  lineFields: ReportField[];
  dimensionsMeta: DimensionDescriptor[];
}

export interface PivotReportConfigurationForRunning extends RunReportConfiguration<PivotGeneralSettings> {
  rows: DimensionField[];
  columns: DimensionField[];
  values: ReportField[];
  sort: SortField[];
}

export interface ChartReportConfigurationForRunning extends RunReportConfiguration<ChartStyleSettings> {
  arguments: DimensionField[];
  values: ReportField[];
  sort: SortField[];
}

export type AnyReportConfigurationForRunning =
  | TabularReportConfigurationForRunning
  | PivotReportConfigurationForRunning
  | ChartReportConfigurationForRunning
  | TileReportConfigurationForRunning
  | ListReportConfigurationForRunning
  | FinancialStatementReportConfigurationForRunning;
export type QueryLoggingItem = {
  resultsOmitted?: boolean;
  queryText: string;
  result: AnyObject[];
  executionTime: number;
  recordsCount: number;
};
export type GroupTotal = {
  filters: Record<string, ValueCell>;
  values: Record<string, ValueCell>;
};
export type TabularColumn = {
  guid: string;
  name: string;
  failedToLoad: boolean;
  title: string | undefined;
  type: ItemDataType;
};
export type FailedMeasure = {
  name: string;
  fieldId?: string;
};
export type TabularDataResponse = {
  snapshot?: TabularSnapshot;
  etag: string;
  errorCode?: string;
  data: TabularRow[];
  totalCount?: number;
  groupTotals: GroupTotal[];
  grandTotals: AnyObject;
  loggingItems?: QueryLoggingItem[];
  columns: TabularColumn[];
  failedMeasures: FailedMeasure[];
};
export type ApiReportResponse = TabularDataResponse & {
  publicResponse: PublicTabularResponse;
};
export type PublicTabularResponse = {
  rows: TabularRow;
  columns: TabularColumn[];
};
export type RunApiReportRequest = RunTabularReportRequest;
export type CrossFilterResponse = {
  values: string[];
};
export type CrossFilterRequest = {
  dimensionName: string;
  conditions: ConditionDescriptor[];
  dimensionNames: string[];
  measureNames: string[];
};

export interface OrganizationReportInfo extends ReportInfo {
  groupName: string;
}

export type MeasureDataset = {
  name: string;
  table: MeasureUnitTable;
  values: { [key: string]: string[] };
};
export type MeasuresStructure = {
  names: string[];
  groups: MeasureGroup[];
};
export type MeasureGroup = {
  caption: string;
  names: string[];
};
export type MetricsStructure = {
  names: string[];
  groups: MeasureGroup[];
};
export type CurrencyDimensionMeta = {
  name: string;
  caption: string;
};
export type CustomFormulaMeta = {
  formula: string;
  format?: NumberFormatterOptions;
  calculationType: CalculationType;
};
export type MetaDataResponse = {
  dataSets: DataSetMetaData[];
};
export type DataSetMetaData = {
  dataSetId: string;
  ledgers: LedgerMeta[];
  dimensionsStructure: DimensionsStructure;
  dimensions: DimensionDescriptor[];
  measuresStructure: MeasuresStructure;
  measures: MeasureDescriptor[];
  functions: FunctionDescription[];
  measureGroups: string[];
  measureSubgroups: MeasureSubgroup[];
  metrics: MetricDescriptor[];
  metricsStructure: MetricsStructure;
  aggregations: AggregationDescriptor[];
  currencyDimensions: CurrencyDimensionMeta[];
  customFormulas: CustomFormulaMeta[];
};
export interface MeasureSubgroup {
  id: string;
  group: string;
  name: string;
}
export interface CreateMeasureSubgroupRequest {
  group: string;
  name: string;
  measureIds?: string[];
}
export interface UpdateMeasureSubgroupRequest {
  name: string;
  measureIds: string[];
}
export interface MeasureSubgroupWithMeasures {
  subgroup: MeasureSubgroup;
  measures: MeasureDescriptor[];
}
export interface MoveCustomMeasureToSubgroupRequest {
  subgroupId: string | null;
}
export interface LedgerMeta {
  name: string;
  caption: string;
}
export type FunctionDescription = {
  name: string;
  fullName: string;
  description: string;
  args: FunctionArgumentDescription[];
};
type FunctionArgumentDescription = {
  name: string;
  description: string;
};

export type BuildPivotReportRequest = Omit<PivotConfiguration, "reportNature"> & {
  reportId: string;
  sessionId: string;
  cacheSessionId?: string;
  withDrilldown: boolean;
  useQueryLogging?: boolean;
};

export type BuildTabularReportRequest = TabularConfiguration & {
  reportId: string;
  sessionId: string;
  cacheSessionId?: string;
  withDrilldown: boolean;
  skip: number;
  take: number;
  calcTotalCount?: boolean;
  calcGroupTotals?: boolean;
  useQueryLogging?: boolean;
};

export type BuildTileReportRequest = TileConfiguration & {
  reportId: string;
  sessionId: string;
  cacheSessionId?: string;
  skip: number;
  take: number;
  useQueryLogging?: boolean;
};

export type ExportParameters = {
  name: string;
  equality?: ConditionEqualityType;
  values: (string | null)[];
};

export type ExportConfig = {
  exportValuesAsDisplayed?: boolean;
  withParameters: boolean;
  parameters: ExportParameters[];
};

export type BuildExportRequest = {
  exportConfig: ExportConfig;
  buildConfig: BuildTabularReportRequest | BuildPivotReportRequest | BuildListReportRequest;
};

export type BuildBalanceSheetExportRequest = {
  exportConfig: ExportConfig;
  buildConfig: BuildBalanceSheetReportRequest;
};

export type RunExportRequest = {
  exportConfig: ExportConfig;
  viewConfig: RunTabularReportRequest | RunPivotReportRequest | RunListReportRequest;
};

export type BuildChartReportRequest = Omit<ChartConfiguration, "reportNature"> & {
  reportId: string;
  sessionId: string;
  cacheSessionId?: string;
  withDrilldown: boolean;
  useQueryLogging?: boolean;
};

export enum ShowPriorPeriods {
  None = "None",
  ByMonth = "ByMonth",
  ByQuarter = "ByQuarter",
  ByYear = "ByYear",
}

export type BalanceSheetSettings = {
  showPriorPeriods?: ShowPriorPeriods;
  priorPeriodCount?: number;
  numberFormat?: NumberFormatterOptions;
};

export enum BalanceSheetIncomeBalanceType {
  BalanceSheet = "BalanceSheet",
  IncomeStatement = "IncomeStatement",
}

export type BcDimensionBreakdownBatch = {
  condition: ConditionFilterDescriptor;
};

export type BuildBalanceSheetReportRequest = {
  reportId: string;
  sessionId: string;
  cacheSessionId: string;
  withDrillDown: boolean;
  useQueryLogging?: boolean;
  incomeBalanceType?: BalanceSheetIncomeBalanceType;
  accountingBook?: string | null;
  conditions: ConditionConfiguration[];
  settings: BalanceSheetSettings;
  breakdownBatches?: BcDimensionBreakdownBatch[];
};

export enum BalanceSheetAccountType {
  GroupStart = "GroupStart",
  Item = "Item",
  GroupTotal = "GroupTotal",
  Header = "Header",
}

export type BalanceSheetColumn = {
  guid: string;
  name: string;
};

export type BalanceSheetRowHeader = {
  accountNo: string;
  caption: string;
  accountType: BalanceSheetAccountType | number;
  indentationLevel: number;
};

export type BalanceSheetCell = {
  value: unknown;
  fieldId: string;
  formattedValue: string;
  drillDownId?: string;
};

export type BalanceSheetRow = {
  id: string;
  header: BalanceSheetRowHeader;
  data: Record<string, BalanceSheetCell>;
};

export type BalanceSheetDataResponse = {
  etag: string;
  errorCode?: string;
  loggingItems?: QueryLoggingItem[];
  columns: BalanceSheetColumn[];
  data: BalanceSheetRow[];
};

export type DrillDownDataRequest = {
  range: RangeRequestParams;
  useQueryLogging?: boolean;
  table: MeasureUnitTable;
  allocated: boolean;
  columns: DrillDownColumn[];
  conditions: ConditionDescriptor[];
  sortingColumns: ColumnSorting[];
  cacheSessionId?: string;
};

export type AttachedDocument = {
  fileName: string;
  fileExtension: string;
  catalogueId: string;
  accessible: boolean;
};

export type CashFlowExportRequest = {
  caption: string;
  sessionId: string;
  itemId: string;
};

export type ChartOfAccountsExportRequest = {
  caption: string;
  sessionId: string;
  itemId: string;
  table: MeasureUnitTable;
  includedOnly: boolean;
  accountingBook: Maybe<string>;
};

export type DrillDownCell = {
  value: unknown;
  fieldName: string;
  formattedValue: string;
  properties?: CellProperties;
};

export type DrillDownRow = {
  id: string;
  cells: Record<string, DrillDownCell>;
};

export type DrillDownDataResponse = ListWithTotal<DrillDownCell[]> & {
  grandTotalItem: DrillDownCell[];
  loggingItems?: QueryLoggingItem[];
};

export type ColumnSorting = DrillDownColumn & {
  sortAsc: boolean;
  caption: string | undefined;
};

export type ExportResponse = {
  token: string;
  fileProcessed: boolean;
  error?: boolean;
  errorText?: string;
};

export type CustomMeasureUnit = {
  variable: string;
  unit: MeasureUnit;
};

export type CustomMeasureDescription = {
  id?: string;
  name: string;
  caption: string;
  group: string;
  subgroupId?: string;
  formula: string;
  calculateBy: CalculateByField;
  units: CustomMeasureUnit[];
  configuration: FieldConfiguration;
  amountType: AmountType;
};

export type LedgerBasisRequest = {
  dimensionNames: string[];
  measureNames: string[];
};

export type LedgerBasisResponse = {
  transactional: boolean;
};

export type ChartOfAccounts = {
  gl: ChartOfAccountsItem[];
  memo: ChartOfAccountsItem[];
};

export type ChartOfAccountsItem = {
  accountNo: string;
  accountName: string;
  accountCategory: string;
  accountCategoryName: string;
  accountType: AccountType;
  accountSubcategory: string;
  incomeBalance: string;
  level: number;
  accountingBook: string | null;
};

export enum AccountType {
  Heading = "Heading",
  BeginTotal = "Begin-Total",
  EndTotal = "End-Total",
  Posting = "Posting",
  Total = "Total",
}

export enum CellDrillDownInfoType {
  Default = "Default",
  Irr = "Irr",
  Metric = "Metric",
  CustomColumn = "CustomColumn",
}

export interface ClientSettings {
  intercomAuthentication?: IntercomAuthSettings;
  accountingBookSettings?: AccountingBookSettings;
  dataSets?: DataSetInfo[];
}

interface IntercomAuthSettings {
  intercomUserId: string;
  intercomUserHash: string;
  intercomUserSignedUp: string;
  intercomAppId: string;
}

export type AccountingBookSettings = {
  multiBook: boolean;
  books: AccountingBook[];
};
export type DataSetInfo = {
  id: string;
  name: string;
};
export type DrillDownConfiguration = {
  primaryDimension: string;
  defaultDimensions: string[];
  apArConfiguration?: DrillDownConfiguration;
  vatConfiguration?: DrillDownConfiguration;
};
export type DrillDownInfoResult = {
  item?: DrillDownInfo;
  notFound: boolean;
};
export type DrillDownInfo = {
  combined: CellDrillDownInfoTypes;
  items: CellDrillDownInfoTypes[];
};
export type CellDrillDownInfoTypes =
  CellDrillDownInfo | IrrDrillDownInfo | MetricDrillDownInfo | CustomColumnDrillDownInfo;
export type CellDrillDownInfoBase = {
  conditions: ConditionDescriptor[];
  drillDownInfoType: CellDrillDownInfoType;
  valueTargetFields?: string[];
};
export type MeasureDrillDownInfo = CellDrillDownInfoBase & {
  drillDownInfoType: CellDrillDownInfoType.Default | CellDrillDownInfoType.Irr;
  table: MeasureUnitTable;
  field: CalculateByField;
  allocated: boolean;
  amountType: AmountType;
};
export type MetricDrillDownInfo = CellDrillDownInfoBase & {
  drillDownInfoType: CellDrillDownInfoType.Metric;
  metricDimension: string;
};
export type CustomColumnDrillDownInfo = CellDrillDownInfoBase & {
  drillDownInfoType: CellDrillDownInfoType.CustomColumn;
  formula?: string;
  drillDownRefs?: CellDrillDownRef[];
};
export type CellDrillDownInfo = MeasureDrillDownInfo & {
  drillDownInfoType: CellDrillDownInfoType.Default;
};
export type IrrDrillDownInfo = MeasureDrillDownInfo & {
  drillDownInfoType: CellDrillDownInfoType.Irr;
  cashFlowValues?: CashFlowValue[];
  referenceDateFieldCaption?: string;
  amountFieldCaption?: string;
};
export type CashFlowTransaction = {
  dimensions: Record<string, string>;
  date: string;
  amount: number;
  amountFormatted: string;
  entryNo: string;
  drillDownInfo?: DrillDownInfo;
};
export type CashFlowValue = {
  isIrr?: boolean;
  transactions?: CashFlowTransaction[];
  date: string;
  amount: number;
  isNav: boolean;
  isBeginningOfPeriod?: boolean;
  description: string;
  dateFormatted: string;
  amountFormatted: string;
  positiveAmountFormatted?: string;
  negativeAmountFormatted?: string;
  drillDownInfo?: DrillDownInfo;
  negativeDrillDownInfo?: DrillDownInfo;
  positiveDrillDownInfo?: DrillDownInfo;
};
export type DrillDownCacheValidationResponse = {
  isCacheValid: boolean;
  expired: boolean;
};
export type ChartOfAccountsDataResponse = {
  items: ChartOfAccountsLedgerItem[];
};
export type ChartOfAccountsLedgerItem = {
  measureUnitTable: MeasureUnitTable;
  items: ChartOfAccountsDataItem[];
};
export type ChartOfAccountsDataItem = ChartOfAccountsItem & {
  amount: number | undefined;
  amountFormatted: string | undefined;
  conditions: ConditionDescriptor[];
  included: boolean;
};
export type UsedByReportSource = "Template" | "API Analytics" | "Report";

export interface UsedByReport {
  id: string;
  reportName: string;
  source: UsedByReportSource;
  clientCode: string;
  organization: string;
}

export interface UsedByReportsResponse {
  reports: UsedByReport[];
  hiddenReportsCount: number;
}

export type DraggableFieldType = ConditionField | ReportField;
export type DraggableMetaType =
  DimensionDescriptor | MeasureDescriptor | CustomColumnDescriptor | MetricDescriptor | AggregationDescriptor;
export type FieldWrapper<T extends DraggableFieldType> = {
  field: T;
  index: number;
  type: string;
};
export type PreviewMode = "maximize" | "default";
export type UsedTablesResponse = UsedTable[];
export type QueryComposition = {
  tables: CompositionTable[];
};
export type DimensionQueryInfo = {
  queryComposition: QueryComposition;
  tableDataModel: TableDataModel;
};
export type TableDataModel = {
  tableId: number;
  relations: TableRelationDataModel[];
  filters: TableFilterDataModel[];
  useInMeasureQueries: boolean;
};
export type TableFilterDataModel = {
  fieldNo: number;
  values: unknown[];
  equalityType: ConditionEqualityType;
};
export type TableRelationDataModel = {
  sourceFieldId: number;
  targetFieldId: number;
  targetTable: TableDataModel;
};
export type CompositionTable = {
  tableNo: number;
  relation: CompositionTableRelation | undefined;
  subTables: CompositionTable[];
};
export type CompositionTableRelation = {
  sourceTableNo: number;
  sourceTableFieldNo: number;
  targetTableNo: number;
  targetTableFieldNo: number;
};
export type MultiFieldId = {
  glFieldId: number;
  memoGlFieldId: number;
  allocatedGlFieldId: number;
  allocatedMemoGlFieldId: number;
};
export type MultiFieldName = {
  glFieldId: string;
  memoGlFieldId: string;
  allocatedGlFieldId: string;
  allocatedMemoGlFieldId: string;
};

export enum FieldContext {
  GL = 1,
  MemoGL = 2,
  AllocatedGL = 4,
  AllocatedMemoGL = 8,
  All = GL | MemoGL | AllocatedGL | AllocatedMemoGL,
}

export type UsedTable = {
  name: string;
  sqlName: string;
  tableNo: number;
};
export type UsedConstraint = {
  name: string;
  columnName: string;
  relationTableName: string;
  relationColumnName: string;
};

export interface DbSchemaResponse {
  schema: DbSchema;
  schemaTree: DevDbSchemaTree;
}

export interface DbSchema {
  id: string;
  createdAt: string;
  dbName: string;
  dbSchemaName: string;
  clientCode: string;
  environment: string;
  companyId: string;
  tables: DbTableSchema[];
}

export interface DbTableSchema {
  name: string;
  sqlName: string;
  tableNo: number;
  columns: DbColumn[];
  constraints: DbTableConstraint[];
}

export interface DbTableConstraint {
  type: DbConstraintTypes;
  name: string;
  columnName: string;
  columnSqlName: string;
  columnNo: number;
  tableNo: number;
  relationTableName: string;
  relationTableSqlName: string;
  relationTableNo: number;
  relationColumnName: string;
  relationColumnSqlName: string;
  relationColumnNo: number;
}

export enum DbConstraintTypes {
  PrimaryKey,
  ForeignKey,
}

export enum DbColumnDataTypes {
  Integer,
  Decimal,
  Code,
  Date,
  Boolean,
  DateFormula,
  Text,
  DateTime,
  GUID,
  Option,
  Media,
  BLOB,
  Time,
  MediaSet,
  BigInteger,
  RecordID,
  Duration,
}

export interface DbColumn {
  caption: string;
  name: string;
  sqlName: string;
  type: DbColumnDataTypes;
  sqlType: string;
  length: number;
  enabled: boolean;
  isPartOfPrimaryKey: boolean;
  no: number;
}

export interface DevDbSchemaTree {
  gl: DevTreeTable;
  memoLedger: DevTreeTable;
  allocatedGl: DevTreeTable;
  allocatedMemoGl: DevTreeTable;
}

export interface DevTreeTable {
  id: number;
  glField: number;
  referenceTables: DevTreeTable[];
  oneToManyReferenceTables: DevTreeTable[];
  constraint: DevDbTableConstraint | null;
}

export interface DevDbColumn {
  caption: string;
  name: string;
  sqlName: string;
  type: DbColumnDataTypes;
  no: number;
}

export interface DevDbTableConstraint {
  type: DbConstraintTypes;
  name: string;
  columnName: string;
  columnSqlName: string;
  columnNo: number;
  relationTableName: string;
  relationTableSqlName: string;
  relationTableNo: number;
  relationColumnName: string;
  relationColumnSqlName: string;
  relationColumnNo: number;
}

export interface DataModelResponse {
  dataModels: DataModel[];
}

export interface DataModel {
  name: string;
  caption: string;
  table: TableDataModel;
  links: DataModelLink[];
}

export interface DataModelLink {
  targetModelName: string;
  fields: DataModelLinkField[];
  context: FieldContext;
}

export interface DataModelLinkField {
  sourceFieldId: number;
  targetFieldId: number;
}

export interface DimensionsResponse {
  dimensions: DimensionModel[];
}

export interface DimensionModel {
  id: string;
  name: string;
  caption: string;
  type: MetaItemType;
  defaultFormat?: NumberFormatterOptions;
  headerDefaultFormat?: NumberFormatterOptions;
  keyField: MultiFieldId;
  keyFieldName: string;
  availableFields: string[];
  displayFields: string[];
  dataModel: MultiFieldName;
  valueNotInDictionaryToolTip?: string;
  clientCode: string;
  category: string;
  udfCategory: string;
  effectiveFrom: string;
  source: UdfSource;
  fieldId?: string;
}

export enum UdfSource {
  BC = 0,
  Portal,
}

// AP/AR Aging Summary Report

export enum ApArAgingSummaryReportType {
  AccountsPayable = "AccountsPayable",
  AccountsReceivable = "AccountsReceivable",
}

export type BuildApArAgingSummaryExportRequest = {
  exportConfig: ExportConfig;
  buildConfig: BuildApArAgingSummaryReportRequest;
};

export type BuildApArAgingSummaryReportRequest = {
  postingDate: string;
  entityNo?: string | null;
  counterpartyTypeCode?: string | null;
  reportType: ApArAgingSummaryReportType;
  sessionId: string;
  cacheSessionId: string;
  useQueryLogging?: boolean;
  numberFormat?: NumberFormatterOptions | null;
  sort?: SortConfiguration[];
};

export type ApArAgingSummaryTabularRow = {
  id: string;
  data: Record<string, TabularDataCell>;
};

export type ApArAgingSummaryTabularColumn = {
  guid: string;
  name: string;
  title: string;
  failedToLoad?: boolean;
};

export type ApArAgingSummaryResponse = {
  etag?: string;
  columns: ApArAgingSummaryTabularColumn[];
  data: ApArAgingSummaryTabularRow[];
  grandTotals?: Record<string, TabularDataCell>;
  loggingItems?: QueryLoggingItem[];
};

// AP/AR Aging Detail Report

export enum ApArAgingDetailReportType {
  AccountsPayable = "AccountsPayable",
  AccountsReceivable = "AccountsReceivable",
}

export type BuildApArAgingDetailExportRequest = {
  exportConfig: ExportConfig;
  buildConfig: BuildApArAgingDetailReportRequest;
};

export type BuildApArAgingDetailReportRequest = {
  postingDate: string;
  entityNo?: string | null;
  counterpartyTypeCode?: string | null;
  reportType: ApArAgingDetailReportType;
  sessionId: string;
  cacheSessionId: string;
  useQueryLogging?: boolean;
  numberFormat?: NumberFormatterOptions | null;
  sort?: SortConfiguration[];
  currentDate: string;
};

export type ApArAgingDetailTabularRow = {
  id: string;
  data: Record<string, TabularDataCell>;
};

export type ApArAgingDetailTabularColumn = {
  guid: string;
  name: string;
  title: string;
  failedToLoad?: boolean;
};

export type ApArAgingDetailGroupTotal = {
  filters: Record<string, ValueCell>;
  values: Record<string, ValueCell>;
};

export type ApArAgingDetailResponse = {
  etag?: string;
  columns: ApArAgingDetailTabularColumn[];
  data: ApArAgingDetailTabularRow[];
  groupTotals?: ApArAgingDetailGroupTotal[];
  grandTotals?: Record<string, TabularDataCell>;
  loggingItems?: QueryLoggingItem[];
};
